import React, { useState, useEffect, useCallback, useRef } from 'react';
import classnames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import useInterval from 'shared/hooks/useInterval';
import { refreshStatus } from './actions';
import { useOrderTrackingSocket } from './hooks';
import { track } from 'shared/models/analytic';
import {
  isCompletedOrder,
  isRejectedOrder,
  isFailedOrder,
  isCanceledOrder
} from 'shared/models/order';
import {
  getProgressSteps,
  getStatusInfo,
  getShippingOption,
  getTrackingCode,
  getTrackingLink,
  getCardInfo
} from './helpers';
import AppWrapper from '../app';
import Header from './Header';
import ProgressSummary from './ProgressSummary';
import OrderSummary from './OrderSummary';
import Map from './statusMap';
import { profileImage } from 'shared/models/user';
import { hidden, tablet_block } from 'scy/display.mod.scss';
import styles from './styles.mod.scss';

export default function OrderTracking() {
  const { user } = useSelector(state => state.login);
  const { order, contactNumber } = useSelector(state => state.orderTracking);
  const orderID = order ? order._id : null;
  const hasLiveTracking = order.tracking_state && order.tracking_state.tracking;

  useEffect(() => {
    track('Order Tracking Viewed');
  }, []);

  const dispatch = useDispatch();
  const refreshOrderStatus = useCallback(() => {
    if (orderID) {
      dispatch(refreshStatus(orderID));
    }
  }, [dispatch, orderID]);
  useInterval(refreshOrderStatus, 20000);

  const [location, setLocation] = useState({});
  const [route, setRoute] = useState([]);
  useOrderTrackingSocket({
    userID: user.objectId,
    orderID: order._id,
    isEnabled: hasLiveTracking,
    handleData: data => {
      if (data && data.location) setLocation(data.location);
      if (data && data.route) setRoute(data.route);
    }
  });

  const timer = useRef();
  useEffect(() => {
    if (order.shipping) return;
    if (isCompletedOrder(order)) {
      timer.current = setTimeout(
        () => (window.location = '/tip/' + order._id),
        5000
      );
    }
    if (isRejectedOrder(order)) {
      timer.current = setTimeout(
        () => (window.location = '/substitution/' + order._id),
        5000
      );
    }
    if (isFailedOrder(order) || isCanceledOrder(order)) {
      timer.current = setTimeout(
        () => (window.location = '/orders/' + order._id),
        5000
      );
    }
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [order]);

  const progressSteps = getProgressSteps(order);
  const statusInfo = getStatusInfo(order);

  const shippingOption = getShippingOption(order);
  const trackingCode = getTrackingCode(order);
  const trackingLink = getTrackingLink(order);

  const cardInfo = getCardInfo(order);

  return (
    <AppWrapper
      renderHeader={props => (
        <Header
          {...props}
          progressStepCount={progressSteps.count}
          progressStep={progressSteps.currentStep}
          statusTitle={statusInfo.title}
          statusDescription={statusInfo.description}
          showStatus={hasLiveTracking}
        />
      )}
      footerProps={{ responsive: false, className: styles.footer }}
    >
      <div className={styles.pageContainer}>
        <div
          className={classnames(styles.map, {
            [hidden]: !hasLiveTracking,
            [tablet_block]: !hasLiveTracking
          })}
        >
          <Map
            order={order}
            user={order.gift_info ? {} : user}
            courierLocation={location}
            route={route}
          />
        </div>
        <div className={styles.content}>
          <ProgressSummary
            contactNumber={contactNumber}
            order={order}
            shippingOption={shippingOption.estimate || shippingOption.name}
            trackingCode={trackingCode}
            trackingLink={trackingLink}
            hasLiveTracking={hasLiveTracking}
          />
          <OrderSummary
            order={order}
            cardInfo={cardInfo}
            shippingName={shippingOption.name}
            shippingEstimate={shippingOption.estimate}
          />
        </div>
      </div>
    </AppWrapper>
  );
}
